import { useQuery } from '@apollo/client'
import gql from 'graphql-tag'
// import { client } from 'graphql/thegraph/apollo'
import { useBlocksFromTimestamps } from 'hooks/useBlocksFromTimestamps'
import { useEthPrices } from 'hooks/useEthPrices'
import { useTheGraphClient } from 'hooks/useTheGraphClient'
import { useDeltaTimestamps } from 'utils/thegraph'

export const TOKENS_BULK = (block: number | undefined, tokens: string[]) => {
  let tokenString = `[`
  tokens.map((address) => {
    return (tokenString += `"${address?.toLowerCase()}",`)
  })
  tokenString += ']'
  const queryString =
    `
    query tokens {
      tokens(where: {id_in: ${tokenString}},` +
    (block ? `block: {number: ${block}} ,` : ``) +
    ` subgraphError: allow) {
        id
        symbol
        name
        derivedETH
        volumeUSD
        volume
        txCount
        totalValueLocked
        feesUSD
        totalValueLockedUSD
        decimals
      }
    }
    `
  return gql(queryString)
}

const LOW_HIGHT_52_WEEK_BULK = (tokenAddress: string, orderDirection: 'asc' | 'desc', date_gte: number) => {
  const queryString = `
      query tokenDayDatas {
        tokenDayDatas(
          first: 1
          orderBy: priceUSD
          orderDirection: ${orderDirection}
          where: {date_gte: ${date_gte}, token_: {id: "${tokenAddress?.toLowerCase()}"}}
        ) {
          id
          priceUSD
        }
      }
  
  `
  return gql(queryString)
}

interface TokenFields {
  id: string
  symbol: string
  name: string
  derivedETH: string
  volumeUSD: string
  volume: string
  feesUSD: string
  txCount: string
  totalValueLocked: string
  totalValueLockedUSD: string
  decimals: number
}

interface TokenDataResponse {
  tokens: TokenFields[]
  bundles: {
    ethPriceUSD: string
  }[]
}

interface CurrentTokenData {
  // token is in some pool on uniswap
  exists: boolean

  // basic token info
  name: string
  symbol: string
  address: string

  // volume
  volumeUSD: number
  txCount: number

  //fees
  feesUSD: number

  // tvl
  tvlToken: number
  tvlUSD: number

  priceUSD: number

  // low hight 52 week
  low52week: number
  hight52week: number
  decimals: number
}

interface Price52WResponse {
  tokenDayDatas: {
    priceUSD: string
    id: string
  }[]
}

/**
 * Fetch top addresses by volume
 */
export function useFetchedCurrentTokenDatas(tokenAddresses: string[]): {
  loading: boolean
  error: boolean
  data:
    | {
        [address: string]: CurrentTokenData
      }
    | undefined
} {
  // get blocks from historic timestamps
  const [t24] = useDeltaTimestamps()

  const { blocks, error: blockError } = useBlocksFromTimestamps([t24])
  const [block24] = blocks ?? []

  const ethPrices = useEthPrices()
  const client = useTheGraphClient()

  const { loading, error, data } = useQuery<TokenDataResponse>(TOKENS_BULK(undefined, tokenAddresses), {
    client: client,
    fetchPolicy: 'no-cache',
  })

  const {
    loading: loading24,
    error: error24,
    data: data24,
  } = useQuery<TokenDataResponse>(TOKENS_BULK(parseInt(block24?.number), tokenAddresses), {
    client: client,
    fetchPolicy: 'no-cache',
  })

  const oneYearAgo = Math.round(Date.now() / 86400 / 1000) * 86400 - 86400 * 365

  const {
    loading: loading_low_52_w,
    error: error_low_52_w,
    data: data_low_52_w,
  } = useQuery<Price52WResponse>(LOW_HIGHT_52_WEEK_BULK(tokenAddresses[0], 'asc', oneYearAgo), {
    client: client,
    fetchPolicy: 'no-cache',
  })

  const {
    loading: loading_hight_52_w,
    error: error_hight_52_w,
    data: data_hight_52_w,
  } = useQuery<Price52WResponse>(LOW_HIGHT_52_WEEK_BULK(tokenAddresses[0], 'desc', oneYearAgo), {
    client: client,
    fetchPolicy: 'no-cache',
  })

  const anyError = Boolean(error || error24 || blockError || error_low_52_w || error_hight_52_w)
  const anyLoading = Boolean(loading || loading24 || loading_low_52_w || loading_hight_52_w)

  if (!ethPrices) {
    return {
      loading: true,
      error: false,
      data: undefined,
    }
  }

  // return early if not all data yet
  if (anyError || anyLoading) {
    return {
      loading: anyLoading,
      error: anyError,
      data: undefined,
    }
  }

  const parsed = data?.tokens
    ? data.tokens.reduce((accum: { [address: string]: TokenFields }, poolData) => {
        accum[poolData.id] = poolData
        return accum
      }, {})
    : {}

  const parsed24 = data24?.tokens
    ? data24.tokens.reduce((accum: { [address: string]: TokenFields }, poolData) => {
        accum[poolData.id] = poolData
        return accum
      }, {})
    : {}

  // format data and calculate daily changes
  const formatted = tokenAddresses.reduce((accum: { [address: string]: CurrentTokenData }, address) => {
    const current: TokenFields | undefined = parsed[address]

    const oneDay: TokenFields | undefined = parsed24[address]

    const volumeUSD =
      current && oneDay
        ? parseFloat(current.volumeUSD) - parseFloat(oneDay.volumeUSD)
        : current
        ? parseFloat(current.volumeUSD)
        : 0

    const tvlUSD = current ? parseFloat(current.totalValueLockedUSD) : 0

    const tvlToken = current ? parseFloat(current.totalValueLocked) : 0
    const priceUSD = current ? parseFloat(current.derivedETH) * ethPrices.current : 0

    const txCount = current ? parseFloat(current.txCount) : 0
    const feesUSD = current ? parseFloat(current.feesUSD) : 0

    const low52week = data_low_52_w
      ? data_low_52_w.tokenDayDatas?.length
        ? parseFloat(data_low_52_w.tokenDayDatas[0].priceUSD)
        : 0
      : 0
    const hight52week = data_hight_52_w
      ? data_hight_52_w.tokenDayDatas?.length
        ? parseFloat(data_hight_52_w.tokenDayDatas[0].priceUSD)
        : 0
      : 0

    accum[address] = {
      exists: !!current,
      address,
      name: current ? current.name : '',
      symbol: current ? current.symbol : '',
      decimals: current ? current?.decimals : 18,
      volumeUSD,
      tvlUSD,
      feesUSD,
      tvlToken,
      priceUSD,
      txCount,
      low52week,
      hight52week,
    }

    return accum
  }, {})

  return {
    loading: anyLoading,
    error: anyError,
    data: formatted,
  }
}
